// ABOUTME: Coordinates cross-document transitions and preserves the scroll level of Peptik format changes.
// ABOUTME: Uses capability checks so partial Safari implementations never reference a missing constructor.
// DATA FLOW: pageswap/pagereveal/pageshow events -> transition validation, scroll restore, and lifecycle cleanup.
// DEPENDENCIES: Browser View Transitions events, sessionStorage, and requestIdleCallback when available.

(function () {
  const viewTransitionRenderBlocker = document.getElementById('view-transition-render-blocker');
  // Remove the view transition render blocker if the user has reduced motion enabled or is on a low power device.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || isLowPowerDevice()) {
    viewTransitionRenderBlocker?.remove();
  } else {
    // If the browser didn't manage to parse the main content quickly, at least let the user see something.
    // We're aiming for the FCP to be under 1.8 seconds since the navigation started.
    const RENDER_BLOCKER_TIMEOUT_MS = Math.max(0, 1800 - performance.now());

    setTimeout(() => {
      viewTransitionRenderBlocker?.remove();
    }, RENDER_BLOCKER_TIMEOUT_MS);
  }

  const idleCallback = typeof requestIdleCallback === 'function' ? requestIdleCallback : setTimeout;

  /**
   * @param {unknown} error
   * @returns {boolean}
   */
  function isExpectedViewTransitionAbort(error) {
    const name = error instanceof DOMException ? error.name : '';
    const message = error instanceof Error ? error.message : String(error);
    return (
      /transition (was skipped|was aborted)|skipping view transition because (skipTransition\(\) was called|viewport size changed)/i.test(
        message
      ) &&
      (name === '' || name === 'AbortError' || name === 'InvalidStateError' || /^(AbortError|InvalidStateError):/.test(message))
    );
  }

  function shouldSkipPeptikNavigation() {
    try {
      return sessionStorage.getItem('peptik-skip-next-view-transition') === 'true';
    } catch (_) {
      return false;
    }
  }

  function finishPeptikNavigation() {
    try {
      sessionStorage.removeItem('peptik-skip-next-view-transition');
    } catch (_) {
      // The transition flag is best-effort when storage is unavailable.
    }
  }

  function restorePeptikFormatScroll() {
    let storedPosition;
    try {
      storedPosition = JSON.parse(sessionStorage.getItem('peptik-format-navigation-scroll') || 'null');
      sessionStorage.removeItem('peptik-format-navigation-scroll');
    } catch (_) {
      return;
    }

    if (
      storedPosition?.path !== window.location.pathname ||
      !Number.isFinite(storedPosition?.y) ||
      storedPosition.y < 0
    ) {
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: storedPosition.y, behavior: 'instant' });
      });
    });
  }

  window.addEventListener('pageshow', restorePeptikFormatScroll, { once: true });

  /** @param {Promise<unknown>} promise */
  function observeViewTransitionPromise(promise) {
    promise.catch((error) => {
      if (!isExpectedViewTransitionAbort(error)) {
        setTimeout(() => {
          throw error;
        });
      }
    });
  }

  function readTransitionType() {
    try {
      return sessionStorage.getItem('custom-transition-type');
    } catch (_) {
      return null;
    }
  }

  /** @param {string | null} transitionType */
  function storeTransitionType(transitionType) {
    try {
      if (transitionType) sessionStorage.setItem('custom-transition-type', transitionType);
      else sessionStorage.removeItem('custom-transition-type');
    } catch (_) {
      // The transition type is an optional animation preference.
    }
  }

  window.addEventListener('pageswap', async (event) => {
    const { viewTransition } = /** @type {PageSwapEvent} */ (event);
    if (viewTransition) {
      observeViewTransitionPromise(viewTransition.ready);
      observeViewTransitionPromise(viewTransition.finished);
      if (viewTransition.updateCallbackDone) {
        observeViewTransitionPromise(viewTransition.updateCallbackDone);
      }
    }

    if (shouldSkipPeptikNavigation() || shouldSkipViewTransition(viewTransition)) {
      /** @type {ViewTransition | null} */ (viewTransition)?.skipTransition();
      return;
    }

    // Cancel view transition on user interaction to improve INP (Interaction to Next Paint)
    const interactionController = new AbortController();
    ['pointerdown', 'keydown'].forEach((eventName) => {
      document.addEventListener(
        eventName,
        () => {
          viewTransition.skipTransition();
        },
        { once: true, signal: interactionController.signal }
      );
    });
    viewTransition.finished.then(
      () => interactionController.abort(),
      () => interactionController.abort()
    );

    // Clean in case you landed on the pdp first. We want to remove the default transition type on the PDP media gallery so there is no duplicate transition name
    document
      .querySelectorAll('[data-view-transition-type]:not([data-view-transition-triggered])')
      .forEach((element) => {
        element.removeAttribute('data-view-transition-type');
      });

    const transitionTriggered = document.querySelector('[data-view-transition-triggered]');
    const transitionType = transitionTriggered?.getAttribute('data-view-transition-type');

    if (transitionType) {
      viewTransition.types.clear();
      viewTransition.types.add(transitionType);
      storeTransitionType(transitionType);
    } else {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
      storeTransitionType(null);
    }
  });

  window.addEventListener('pagereveal', async (event) => {
    const { viewTransition } = /** @type {PageRevealEvent} */ (event);
    if (viewTransition) {
      observeViewTransitionPromise(viewTransition.ready);
      observeViewTransitionPromise(viewTransition.finished);
      if (viewTransition.updateCallbackDone) {
        observeViewTransitionPromise(viewTransition.updateCallbackDone);
      }
    }

    if (shouldSkipPeptikNavigation()) {
      finishPeptikNavigation();
      viewTransitionRenderBlocker?.remove();
      /** @type {ViewTransition | null} */ (viewTransition)?.skipTransition();
      return;
    }

    if (shouldSkipViewTransition(viewTransition)) {
      /** @type {ViewTransition | null} */ (viewTransition)?.skipTransition();
      return;
    }

    const customTransitionType = readTransitionType();

    if (customTransitionType) {
      viewTransition.types.clear();
      viewTransition.types.add(customTransitionType);

      try {
        await viewTransition.finished;
      } catch (error) {
        if (!isExpectedViewTransitionAbort(error)) throw error;
      }

      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');

      idleCallback(() => {
        storeTransitionType(null);
        document.querySelectorAll('[data-view-transition-type]').forEach((element) => {
          element.removeAttribute('data-view-transition-type');
        });
      });
    } else {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
    }
  });

  /**
   * @param {ViewTransition | null} viewTransition
   * @returns {boolean}
   */
  function shouldSkipViewTransition(viewTransition) {
    return !viewTransition || !viewTransition.types || isLowPowerDevice();
  }

  /*
   * We can't import this logic from utilities.js here, but we should keep them in sync.
   */
  function isLowPowerDevice() {
    /* Skip ESLint compatibility check. Number(undefined) <= 2 is always false anyway. */
    /* eslint-disable-next-line compat/compat */
    return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
  }
})();
